import React from 'react';
import ContactInformationFormStore from './ContactInformationFormStore';
import { observer } from 'mobx-react';
import { FormGroup, Label, Col, Input, FormFeedback, FormText } from 'reactstrap';
import ErrorList from '~/components/ErrorList';
import { countries } from '~/lib/helper/CountryHelper';

interface ContactInformationFormProps {
    store: ContactInformationFormStore;
    skipEmail?: boolean;
    disabled?: boolean;
}

@observer
export default class ContactInformationForm extends React.Component<ContactInformationFormProps> {
    render() {
        const contactForm = this.props.store;
        const disabled = this.props.disabled;
        return (
            <>
                <FormGroup row>
                    <Label for={contactForm.idFor('name')} sm={3}>
                        Name
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('name')}
                            {...contactForm.inputProps('name')}
                            autoComplete="name"
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('name')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('organization')} sm={3}>
                        Organization
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('organization')}
                            {...contactForm.inputProps('organization')}
                            autoComplete="organization"
                            disabled={disabled}
                        />
                        <FormText>Optional</FormText>
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('organization')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                {!this.props.skipEmail && (
                    <>
                        <FormGroup row>
                            <Label for={contactForm.idFor('email')} sm={3}>
                                Account Email
                            </Label>
                            <Col sm={9}>
                                <Input
                                    id={contactForm.idFor('email')}
                                    type="email"
                                    {...contactForm.inputProps('email')}
                                    disabled={disabled}
                                />
                                <FormFeedback>
                                    <ErrorList errors={contactForm.errorsFor('email')} />
                                </FormFeedback>
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Label for={contactForm.idFor('invoice_email')} sm={3}>
                                Invoice Email
                            </Label>
                            <Col sm={9}>
                                <Input
                                    id={contactForm.idFor('invoice_email')}
                                    type="email"
                                    {...contactForm.inputProps('invoice_email')}
                                    disabled={disabled}
                                />
                                <FormText>Optional - if blank, invoices will be sent to account email</FormText>
                                <FormFeedback>
                                    <ErrorList errors={contactForm.errorsFor('invoice_email')} />
                                </FormFeedback>
                            </Col>
                        </FormGroup>
                    </>
                )}

                <FormGroup row>
                    <Label for={contactForm.idFor('address1')} sm={3}>
                        Address
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('address1')}
                            {...contactForm.inputProps('address1')}
                            autoComplete="address-line1"
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('address1')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('address2')} sm={3}>
                        Address Line 2
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('address2')}
                            {...contactForm.inputProps('address2')}
                            autoComplete="address-line2"
                            disabled={disabled}
                        />
                        <FormText>Optional</FormText>
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('address2')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('city')} sm={3}>
                        City
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('city')}
                            {...contactForm.inputProps('city')}
                            autoComplete="address-level2"
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('city')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('state')} sm={3}>
                        State
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('state')}
                            {...contactForm.inputProps('state')}
                            autoComplete="address-level1"
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('state')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('postal_code')} sm={3}>
                        Post Code
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('postal_code')}
                            {...contactForm.inputProps('postal_code')}
                            autoComplete="postal-code"
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('postal_code')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('country')} sm={3}>
                        Country
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('country')}
                            type="select"
                            {...contactForm.inputProps('country')}
                            autoComplete="country-name"
                            disabled={disabled}
                        >
                            <option value="">Select a country...</option>
                            {countries.map((country) => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('country')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label for={contactForm.idFor('phone_number')} sm={3}>
                        Mobile Number
                    </Label>
                    <Col sm={9}>
                        <Input
                            id={contactForm.idFor('phone_number')}
                            type="tel"
                            autoComplete="tel"
                            placeholder="eg +61 5555 5555"
                            {...contactForm.inputProps('phone_number')}
                            disabled={disabled}
                        />
                        <FormFeedback>
                            <ErrorList errors={contactForm.errorsFor('phone_number')} />
                        </FormFeedback>
                    </Col>
                </FormGroup>
            </>
        );
    }
}
